import React, { useMemo, useState } from 'react';
import _ from 'lodash';
import { Box } from '@good/ui/core';
import moment from 'moment';
import { GoodTable } from '@good/ui/templates';

import { HeaderRow } from './components/HeaderRow';
import { GroupColumn } from './components/GroupColumn';
import { RevenueColumn } from './components/RevenueColumn';
import { HoursWorkedColumn } from './components/HoursWorkedColumn';
import { getEntireWeekForDay, uniqueCustomers, uniqueWorkers } from '../components/roster-utilities';
import { CustomerItemRow } from './components/customer-item-row';
import { WorkerItemRow } from './components/worker-item-row';
import { OpenSlotsRow } from './components/open-slots-row';
import { ShiftSlotStatus } from 'utilities/enum-utils';
import { IRosterBooking, IRosterCustomer, IRosterShift, IRosterWorker } from '../types';

interface RosterWeeklyProps {
  bookings: IRosterBooking[];
  shifts: IRosterShift[];
  selectedWeek?: [Date, Date];
  showOpenSlots?: boolean;
  showCustomers?: boolean;
  showWorkers?: boolean;
  customersList?: IRosterCustomer[];
  customersDisplayMode?: 'dynamic' | 'fixed';
  workersList?: IRosterWorker[];
  workersDisplayMode?: 'dynamic' | 'fixed';
  onClickHeaderDay?: (day: Date) => void;
  isFetching?: boolean;
}

export const RosterWeekly = ({
  bookings,
  shifts,
  selectedWeek = [new Date(), new Date()],
  showOpenSlots = true,
  showCustomers = true,
  showWorkers = true,
  customersList,
  customersDisplayMode = 'dynamic',
  workersList,
  workersDisplayMode = 'dynamic',
  onClickHeaderDay = () => {},
}: RosterWeeklyProps) => {
  const [isCustomersOpen, setCustomersOpen] = useState(true);
  const [isWorkersOpen, setWorkersOpen] = useState(true);
  const today = selectedWeek[0];
  const weekdays = getEntireWeekForDay(today, moment(selectedWeek[1]).diff(today, 'days') + 1);
  const openShifts = useMemo(
    () => (showOpenSlots ? _.filter(shifts, (shift) => shift.shiftSlotStatus === ShiftSlotStatus.UNASSIGNED) : []),
    [shifts, showOpenSlots],
  );
  const publishedUnfilledShifts = useMemo(() => {
    const filtered = showOpenSlots
      ? _.filter(shifts, (shift) => {
          const isEmpty = _.isEmpty(shift.supportWorkerId);
          const isPublishing = shift.shiftSlotStatus === ShiftSlotStatus.PUBLISHING;
          const isPublished = shift.shiftSlotStatus === ShiftSlotStatus.PUBLISHED;
          const shouldInclude = isEmpty && (isPublishing || isPublished);

          return shouldInclude;
        })
      : [];

    return filtered;
  }, [shifts, showOpenSlots]);
  const combinedOpenShifts = useMemo(
    () => [...openShifts, ...publishedUnfilledShifts],
    [openShifts, publishedUnfilledShifts],
  );
  const assignedShifts = useMemo(
    () =>
      _.filter(
        shifts,
        (shift) => shift.shiftSlotStatus !== ShiftSlotStatus.UNASSIGNED && !_.isEmpty(shift.supportWorkerId),
      ),
    [shifts],
  );
  const customers = useMemo(
    () => (customersDisplayMode === 'fixed' ? customersList : uniqueCustomers(bookings)),
    [bookings, customersList, customersDisplayMode],
  );
  const workers = useMemo(
    () => (workersDisplayMode === 'fixed' ? workersList : uniqueWorkers(assignedShifts)),
    [assignedShifts, workersList, workersDisplayMode],
  );
  const hasOpenShifts = _.size(combinedOpenShifts) > 0;

  return (
    <>
      <Box h='100%'>
        <GoodTable withRowBorders withColumnBorders stickyHeader verticalSpacing='xs' horizontalSpacing='xs'>
          <HeaderRow days={weekdays.map((w) => w.toDate())} onClickDay={onClickHeaderDay} />{' '}
          <GoodTable.Tbody>
            <GoodTable.Tr>
              <GoodTable.Td colSpan={weekdays.length + 1} p={0} />
            </GoodTable.Tr>
            {showCustomers && (
              <>
                <GoodTable.Tr>
                  <GoodTable.Td sticky bg='gray.0'>
                    <GroupColumn isOpen={isCustomersOpen} setOpen={setCustomersOpen} text={'Customers'} />
                  </GoodTable.Td>
                  {weekdays.map((_, idx) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                    <GoodTable.Td key={idx} bg='gray.0'>
                      <RevenueColumn showRevenue={false} revenueAmount={5127.6} />
                    </GoodTable.Td>
                  ))}
                </GoodTable.Tr>
                {isCustomersOpen &&
                  customers?.map((customer, idx) => (
                    <CustomerItemRow
                      customer={customer}
                      bookings={bookings}
                      days={weekdays}
                      // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                      key={idx}
                    />
                  ))}
              </>
            )}
            {showWorkers && (
              <>
                <GoodTable.Tr>
                  <GoodTable.Td sticky bg='gray.0'>
                    <GroupColumn isOpen={isWorkersOpen} setOpen={setWorkersOpen} text={'Team members'} />
                  </GoodTable.Td>

                  {weekdays.map((_, idx) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                    <GoodTable.Td key={idx} bg='gray.0'>
                      <HoursWorkedColumn hoursTotal={8} costTotal={135.56} showCost={false} />
                    </GoodTable.Td>
                  ))}
                </GoodTable.Tr>

                {isWorkersOpen && (
                  <>
                    {hasOpenShifts && <OpenSlotsRow openShifts={combinedOpenShifts} days={weekdays} />}
                    {workers?.map((worker, idx) => (
                      <WorkerItemRow
                        worker={worker}
                        shifts={assignedShifts}
                        days={weekdays}
                        // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                        key={idx}
                      />
                    ))}
                  </>
                )}
              </>
            )}
          </GoodTable.Tbody>
        </GoodTable>
      </Box>
    </>
  );
};
